'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function HealthyWeightCalculator() {
  const t = useTranslations("tools.healthyWeightCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [unit, setUnit] = useState<'imperial' | 'metric'>('imperial');
  const [inputs, setInputs] = useState({
    heightFeet: 5,
    heightInches: 10,
    heightCm: 178,
  });
  const [results, setResults] = useState<{
    minWeight: number;
    maxWeight: number;
    unit: string;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let heightInMeters: number;

    if (unit === 'imperial') {
      const totalInches = (inputs.heightFeet * 12) + inputs.heightInches;
      heightInMeters = totalInches * 0.0254;
    } else {
      heightInMeters = inputs.heightCm / 100;
    }

    if (heightInMeters < 0.5 || heightInMeters > 2.5) {
      alert(t('invalidHeight'));
      return;
    }

    const minBMI = 18.5;
    const maxBMI = 24.9;

    const minWeightKg = minBMI * heightInMeters * heightInMeters;
    const maxWeightKg = maxBMI * heightInMeters * heightInMeters;

    if (unit === 'imperial') {
      setResults({
        minWeight: parseFloat((minWeightKg * 2.20462).toFixed(1)),
        maxWeight: parseFloat((maxWeightKg * 2.20462).toFixed(1)),
        unit: 'lbs',
      });
    } else {
      setResults({
        minWeight: parseFloat(minWeightKg.toFixed(1)),
        maxWeight: parseFloat(maxWeightKg.toFixed(1)),
        unit: 'kg',
      });
    }
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
    },
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const unitToggleStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    gap: '1rem',
    marginBottom: '2rem',
    background: '#f8fafc',
    padding: '0.5rem',
    borderRadius: '0.5rem',
  };

  const unitButtonStyle = (isActive: boolean) => getResponsiveStyle(
    {
      flex: 1,
      border: isActive ? '2px solid #2563eb' : '2px solid transparent',
      background: isActive ? 'white' : 'transparent',
      color: isActive ? '#2563eb' : '#64748b',
      fontWeight: 600,
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: isActive ? '0 2px 8px rgba(37, 99, 235, 0.2)' : 'none',
    },
    { padding: '0.625rem 1rem', fontSize: '0.9rem' },
    { padding: '0.75rem 1.25rem', fontSize: '0.95rem' },
    { padding: '0.75rem 1.5rem', fontSize: '1rem' }
  );

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', color: colors.textPrimary, fontWeight: 600 },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const inputRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  };

  const inputWrapperStyle: React.CSSProperties = {
    position: 'relative',
    flex: 1,
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.8125rem 1.125rem', fontSize: '1rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' }
  );

  const inputUnitStyle: React.CSSProperties = {
    position: 'absolute',
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontSize: '0.9rem',
    fontWeight: 500,
    pointerEvents: 'none',
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.9375rem 1.75rem', fontSize: '1.0625rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #e2e8f0',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: colors.textPrimary, textAlign: 'center' as const },
    { fontSize: '1.35rem' },
    { fontSize: '1.425rem' },
    { fontSize: '1.5rem' }
  );

  const weightRangeCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const weightRangeTitleStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb', marginBottom: '1rem', textAlign: 'center' as const },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const weightRangeValueStyle = getResponsiveStyle(
    { fontWeight: 800, color: colors.textPrimary, textAlign: 'center' as const, marginBottom: '0.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '1.875rem' },
    { fontSize: '2rem' }
  );

  const weightRangeSubtitleStyle = getResponsiveStyle(
    { textAlign: 'center' as const, color: colors.textSecondary },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const bmiCategoriesStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginTop: '1.5rem',
  };

  const bmiCategoryStyle = (borderColor: string) => ({
    background: 'white',
    padding: '1rem',
    borderRadius: '0.5rem',
    borderLeft: `4px solid ${borderColor}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  });

  const bmiCategoryTitleStyle = (color: string) => getResponsiveStyle(
    { fontWeight: 700, marginBottom: '0.25rem', color },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const bmiCategoryRangeStyle = getResponsiveStyle(
    { color: colors.textSecondary },
    { fontSize: '0.8rem' },
    { fontSize: '0.825rem' },
    { fontSize: '0.85rem' }
  );

  const infoBoxStyle = getResponsiveStyle(
    {
      background: '#eff6ff',
      borderLeft: '4px solid #2563eb',
      borderRadius: '0.5rem',
      marginTop: '1.5rem',
    },
    { padding: '0.875rem', fontSize: '0.85rem' },
    { padding: '0.9375rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '0.9rem' }
  );

  const infoBoxPStyle: React.CSSProperties = {
    margin: 0,
    color: '#1e40af',
    lineHeight: 1.6,
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: 0 },
    { fontSize: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const strongStyle: React.CSSProperties = {
    color: colors.primary,
    fontWeight: 600,
  };

  const referenceListStyle: React.CSSProperties = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  };

  const referenceItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const referenceLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>{t('toolTitle')}</h2>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={unitToggleStyle}>
              <button
                type="button"
                style={unitButtonStyle(unit === 'imperial')}
                onClick={() => setUnit('imperial')}
              >
                {t('usUnits')}
              </button>
              <button
                type="button"
                style={unitButtonStyle(unit === 'metric')}
                onClick={() => setUnit('metric')}
              >
                {t('metricUnits')}
              </button>
            </div>

            {unit === 'imperial' ? (
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('heightLabel')}</label>
                <div style={inputRowStyle}>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      style={inputStyle}
                      min="0"
                      max="8"
                      step="1"
                      value={inputs.heightFeet}
                      onChange={(e) => setInputs({ ...inputs, heightFeet: parseFloat(e.target.value) || 0 })}
                      required
                    />
                    <span style={inputUnitStyle}>{t('feet')}</span>
                  </div>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      style={inputStyle}
                      min="0"
                      max="11"
                      step="1"
                      value={inputs.heightInches}
                      onChange={(e) => setInputs({ ...inputs, heightInches: parseFloat(e.target.value) || 0 })}
                      required
                    />
                    <span style={inputUnitStyle}>{t('inches')}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('heightLabel')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    min="50"
                    max="250"
                    step="0.1"
                    value={inputs.heightCm}
                    onChange={(e) => setInputs({ ...inputs, heightCm: parseFloat(e.target.value) || 0 })}
                    required
                  />
                  <span style={inputUnitStyle}>{t('cm')}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(37, 99, 235, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>{t('resultsTitle')}</h3>

              <div style={weightRangeCardStyle}>
                <div style={weightRangeTitleStyle}>{t('recommendedRange')}</div>
                <div style={weightRangeValueStyle}>
                  {results.minWeight} - {results.maxWeight} {results.unit}
                </div>
                <div style={weightRangeSubtitleStyle}>{t('basedOnBMI')}</div>
              </div>

              <div style={bmiCategoriesStyle}>
                <div style={bmiCategoryStyle('#3b82f6')}>
                  <div style={bmiCategoryTitleStyle('#3b82f6')}>{t('underweight')}</div>
                  <div style={bmiCategoryRangeStyle}>{t('underweightRange')}</div>
                </div>
                <div style={bmiCategoryStyle('#10b981')}>
                  <div style={bmiCategoryTitleStyle('#10b981')}>{t('healthyWeight')}</div>
                  <div style={bmiCategoryRangeStyle}>{t('healthyWeightRange')}</div>
                </div>
                <div style={bmiCategoryStyle('#f59e0b')}>
                  <div style={bmiCategoryTitleStyle('#f59e0b')}>{t('overweight')}</div>
                  <div style={bmiCategoryRangeStyle}>{t('overweightRange')}</div>
                </div>
                <div style={bmiCategoryStyle('#ef4444')}>
                  <div style={bmiCategoryTitleStyle('#ef4444')}>{t('obese')}</div>
                  <div style={bmiCategoryRangeStyle}>{t('obeseRange')}</div>
                </div>
              </div>

              <div style={infoBoxStyle}>
                <p style={infoBoxPStyle}>
                  <strong>{t('noteLabel')}</strong> {t('noteText')}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li>{t('howToStep1')}</li>
          <li>{t('howToStep2')}</li>
          <li>{t('howToStep3')}</li>
          <li>{t('howToStep4')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li><strong style={strongStyle}>{t('insight1Title')}</strong> {t('insight1Text')}</li>
          <li><strong style={strongStyle}>{t('insight2Title')}</strong> {t('insight2Text')}</li>
          <li><strong style={strongStyle}>{t('insight3Title')}</strong> {t('insight3Text')}</li>
          <li><strong style={strongStyle}>{t('insight4Title')}</strong> {t('insight4Text')}</li>
          <li><strong style={strongStyle}>{t('insight5Title')}</strong> {t('insight5Text')}</li>
          <li><strong style={strongStyle}>{t('insight6Title')}</strong> {t('insight6Text')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>
            {t('faq1Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>
            {t('faq2Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>
            {t('faq3Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>
            {t('faq4Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.375rem', marginBottom: '0.5rem' }}>
            {t('faq5Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={referenceListStyle}>
          <li style={referenceItemStyle}>
            1. <a href="https://www.aarp.org/health/healthy-living/bmi-calculator/" style={referenceLinkStyle} target="_blank" rel="noopener noreferrer">
              BMI Calculator: Measure Body Mass Index and Fat - AARP
            </a>
          </li>
          <li style={referenceItemStyle}>
            2. <a href="https://www.heart.org/en/healthy-living/healthy-eating/losing-weight/bmi-in-adults" style={referenceLinkStyle} target="_blank" rel="noopener noreferrer">
              Body Mass Index in Adults | American Heart Association
            </a>
          </li>
          <li style={referenceItemStyle}>
            3. <a href="https://www.cancer.org/cancer/risk-prevention/diet-physical-activity/body-weight-and-cancer-risk/body-mass-index-bmi-calculator.html" style={referenceLinkStyle} target="_blank" rel="noopener noreferrer">
              Body Mass Index (BMI) Calculator - American Cancer Society
            </a>
          </li>
          <li style={referenceItemStyle}>
            4. <a href="https://www.healthline.com/health/ideal-body-weight" style={referenceLinkStyle} target="_blank" rel="noopener noreferrer">
              Ideal Body Weight: What Is It and How to Calculate - Healthline
            </a>
          </li>
          <li style={referenceItemStyle}>
            5. <a href="https://my.clevelandclinic.org/health/articles/9464-body-mass-index-bmi" style={referenceLinkStyle} target="_blank" rel="noopener noreferrer">
              BMI (Body Mass Index): What It Is & How To Calculate
            </a>
          </li>
          <li style={referenceItemStyle}>
            6. <a href="https://www.cdc.gov/bmi/about/index.html" style={referenceLinkStyle} target="_blank" rel="noopener noreferrer">
              About Body Mass Index (BMI) - CDC
            </a>
          </li>
          <li style={referenceItemStyle}>
            7. <a href="https://www.nhlbi.nih.gov/health/heart-healthy-living/healthy-weight" style={referenceLinkStyle} target="_blank" rel="noopener noreferrer">
              Heart-Healthy Living - Aim for a Healthy Weight - NHLBI
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}